import { useState, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import PhotoCropModal from '../../components/ui/PhotoCropModal';
import { toast } from '../../components/ui/Toast';
import { IconCheck, IconCamera } from '@tabler/icons-react';

export default function PersonalDetails() {
  const { user, userProfile, orgId, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name || '');
      setJobTitle(userProfile.jobTitle || '');
      setPhone(userProfile.phone || '');
    }
  }, [userProfile]);

  const handlePhotoSave = async (blob) => {
    try {
      const storageRef = ref(storage, 'organisations/' + orgId + '/avatars/' + user.uid);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'organisations', orgId, 'users', user.uid), {
        photoURL: url, updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      toast.success('Photo updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update photo');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'users', user.uid), {
        name: name.trim(),
        jobTitle: jobTitle.trim() || null,
        phone: phone.trim() || null,
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      toast.success('Profile updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update profile');
    }
    setSaving(false);
  };

  const roleLabel = (r) => {
    if (r === 'org_admin') return 'Admin';
    if (r === 'manager') return 'Manager';
    return 'Operator';
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 560, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 24 }}>Personal details</h1>

        {/* Photo */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>Profile photo</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ position: 'relative' }}>
              <Avatar name={userProfile?.name} photoURL={userProfile?.photoURL} size="xl" />
              <div
                onClick={() => setShowCropModal(true)}
                style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 26, height: 26, borderRadius: '50%',
                  background: 'var(--color-brand)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <IconCamera size={13} color="#fff" stroke={1.5} />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: 'var(--color-text-primary)', marginBottom: 4 }}>{userProfile?.name}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 8 }}>
                {roleLabel(userProfile?.role)} · {userProfile?.siteName || 'All sites'}
              </div>
              <Button size="sm" variant="secondary" onClick={() => setShowCropModal(true)}>
                Change photo
              </Button>
            </div>
          </div>
        </Card>

        {/* Details */}
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>Details</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Full name <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Email</label>
            <input type="email" value={userProfile?.email || ''} disabled style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Job title</label>
            <input type="text" placeholder="e.g. Farm Manager" value={jobTitle} onChange={e => setJobTitle(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Phone number</label>
            <input type="tel" placeholder="+44 7700 900 000" value={phone} onChange={e => setPhone(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
        </Card>

        <Button variant="primary" onClick={handleSave} disabled={saving} loading={saving} icon={<IconCheck size={14} stroke={1.5} />}>
          {saving ? 'Saving...' : 'Save changes'}
        </Button>
      </div>

      {showCropModal && (
        <PhotoCropModal onSave={handlePhotoSave} onClose={() => setShowCropModal(false)} />
      )}
    </Layout>
  );
}